import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ChartModule } from 'angular-highcharts';
import { TranslocoModule } from '@jsverse/transloco';

// Directives
import { ScrollDirective } from '@bazis/shared/directives/scroll.directive';
import { ClickOutsideDirective } from '@bazis/shared/directives/click-outside.directive';
import { ColorDirective } from '@bazis/shared/directives/color.directive';
import { BackgroundDirective } from '@bazis/shared/directives/background.directive';
import { IfPropertyDirective } from '@bazis/shared/directives/if-property.directive';
import { IfControlDirective } from '@bazis/shared/directives/if-control.directive';
import { StickyDirective } from '@bazis/shared/directives/sticky.directive';
import { InView } from '@bazis/shared/directives/in-view.directive';
import { FixNumberInputDirective } from '@bazis/shared/directives/fix-number-input.directive';
import { LocalRouterLinkDirective } from '@bazis/shared/directives/local-router-link.directive';
import { SwipeDirective } from '@bazis/shared/directives/swipe.directive';
import { AnchorDirective } from '@bazis/shared/directives/anchor.directive';
import { DropZoneDirective } from '@bazis/shared/directives/drop-zone.directive';
import { HrefProcessorDirective } from '@bazis/shared/directives/href-processor.directive';
import { EllipsisDirective } from '@bazis/shared/directives/ellipsis.directive';

// Pipes
import { EntityPipe } from '@bazis/shared/pipes/entity.pipe';
import { FileSizePipe } from '@bazis/shared/pipes/file-size.pipe';
import { SafePipe } from '@bazis/shared/pipes/safe.pipe';
import { StatusPipe } from '@bazis/shared/pipes/status.pipe';
import { TransitPipe } from '@bazis/shared/pipes/transit.pipe';
import { EnumPipe } from '@bazis/shared/pipes/enum.pipe';
import { PluralPipe } from '@bazis/shared/pipes/plural.pipe';
import { OrganizationPipe } from '@bazis/shared/pipes/organization.pipe';
import { LocalDatePipe } from '@bazis/shared/pipes/local-date.pipe';
import { LocalNumberPipe } from '@bazis/shared/pipes/local-number.pipe';
import { PhonePipe } from '@bazis/shared/pipes/phone.pipe';
import { SnilsPipe } from '@bazis/shared/pipes/snils.pipe';
import { ReversePipe } from '@bazis/shared/pipes/reverse.pipe';
import { EnumsPipe } from '@bazis/shared/pipes/enums.pipe';
import { ScheduleTimePipe } from '@bazis/shared/pipes/schedule-time.pipe';

// Modules
import { DatepickerModule } from '@bazis/shared/components/web/datepicker/datepicker.module';
import { TimepickerModule } from '@bazis/shared/components/web/timepicker/timepicker.module';
import { IconModule } from '@bazis/shared/components/web/icon/icon.module';
import { ColModule } from '@bazis/shared/components/web/grid/col/col.module';
import { GridModule } from '@bazis/shared/components/web/grid/grid/grid.module';
import { RowModule } from '@bazis/shared/components/web/grid/row/row.module';
import { ToggleModule } from '@bazis/shared/components/web/toggle/toggle.module';
import { TabBtnModule } from '@bazis/shared/components/web/tabs/tab-btn/tab-btn.module';
import { TabsModule } from '@bazis/shared/components/web/tabs/tabs.module';
import { ChipModule } from '@bazis/shared/components/web/chip/chip.module';
import { BadgeModule } from '@bazis/shared/components/web/badge/badge.module';
import { ButtonModule } from '@bazis/shared/components/web/button/button.module';
import { LogoModule } from '@bazis/shared/components/web/logo/logo.module';
import { LinkModule } from '@bazis/shared/components/web/link/link.module';
import { TooltipModule } from '@bazis/shared/components/web/tooltip/tooltip.module';
import { ToolbarModule } from '@bazis/shared/components/web/toolbar/toolbar.module';
import { StepModule } from '@bazis/shared/components/web/step/step.module';
import { AccordionModule } from '@bazis/shared/components/web/accordion/accordion.module';
import { TripLineModule } from '@bazis/shared/components/web/trip-line/trip-line.module';
import { SkeletonModule } from '@bazis/shared/components/web/skeleton/skeleton.module';
import { RangeModule } from '@bazis/shared/components/web/range/range.module';
import { ThumbnailModule } from '@bazis/shared/components/web/thumbnail/thumbnail.module';
import { CardValueModule } from '@bazis/shared/components/web/card-value/card-value.module';
import { SignStatusModule } from '@bazis/shared/components/web/sign-status/sign-status.module';
import { LayoutPressFooterModule } from '@bazis/shared/components/web/layout-press-footer/layout-press-footer.module';
import { InterrogationModule } from '@bazis/shared/components/web/interrogation/interrogation.module';
import { TotopModule } from '@bazis/shared/components/web/totop/totop.module';
import { SegmentModule } from '@bazis/shared/components/web/segment/segment.module';
import { StarModule } from '@bazis/shared/components/web/star-rating/star/star.module';
import { StarsModule } from '@bazis/shared/components/web/star-rating/stars/stars.module';
import { LabelModule } from '@bazis/shared/components/web/label/label.module';
import { AvatarModule } from '@bazis/shared/components/web/avatar/avatar.module';

// Components
import { InfiniteListComponent } from '@bazis/shared/components/infinite-list.component';
import { InfiniteListSortSelectedComponent } from '@bazis/shared/components/infinite-list-sort-selected.component';
import { BreadcrumbsComponent } from '@bazis/shared/components/breadcrumbs/breadcrumbs.component';
import { BazisAlertComponent } from '@bazis/shared/components/alert/alert.component';
import { ToastComponent } from '@bazis/shared/components/toast/toast.component';
import { ModalComponent } from '@bazis/shared/components/modal/modal.component';
import { GlobalLoaderComponent } from '@bazis/shared/components/loaders/global-loader.component';
import { DefaultLoaderComponent } from '@bazis/shared/components/loaders/default-loader.component';
import { ModalsComponent } from '@bazis/shared/components/modal/modals.component';
import { CoordinatesComponent } from '@bazis/shared/components/coordinates/coordinates.component';
import { EntityStatusListComponent } from '@bazis/shared/components/entity-status-list.component';
import { GalleryModalComponent } from '@bazis/shared/components/modals/gallery-modal.component';
import { VehicleNumberComponent } from '@bazis/shared/components/vehicle-number/vehicle-number.component';
import { GallerySliderComponent } from '@bazis/shared/components/gallery/gallery-slider.component';
import { GalleryComponent } from '@bazis/shared/components/gallery/gallery.component';
import { StatusComponent } from '@bazis/shared/components/status/status.component';
import { FileInlineComponent } from '@bazis/shared/components/file/file-inline.component';
import { CardContactComponent } from '@bazis/shared/components/card-contact/card-contact.component';
import { DateplateComponent } from '@bazis/shared/components/dateplate/dateplate.component';
import { GallerySingleSliderComponent } from '@bazis/shared/components/gallery/gallery-single-slider.component';
import { GallerySkeletonComponent } from '@bazis/shared/components/gallery/gallery-skeleton.component';
import { EntityStatusListSkeletonComponent } from '@bazis/shared/components/entity-status-list-skeleton.component';
import { EmptyComponent } from '@bazis/shared/components/empty/empty.component';
import { CookieComponent } from '@bazis/shared/components/cookie/cookie.component';
import { TabsSliderComponent } from '@bazis/shared/components/tabs-slider/tabs-slider.component';
import { SimpleModalComponent } from '@bazis/shared/components/modals/simple-modal.component';
import { SliderComponent } from '@bazis/shared/components/slider/slider.component';
import { DummyMobileComponent } from '@bazis/shared/components/dummy-mobile/dummy-mobile.component';
import { DummyModalComponent } from '@bazis/shared/components/modals/dummy-modal.component';
import { LoadMoreListComponent } from '@bazis/shared/components/loadmore-list.component';
import { TimelineComponent } from '@bazis/shared/components/timeline/timeline.component';
import { DocumentAccordeonComponent } from '@bazis/shared/components/document-accordeon/document-accordeon.component';
import { SignedFileInlineComponent } from '@bazis/shared/components/file/signed-file-inline.component';
import { CollapsibleToMoreComponent } from '@bazis/shared/components/collapsible-to-more/collapsible-to-more.component';
import { ContextMenuComponent } from '@bazis/shared/components/context-menu/context-menu.component';
import { ContextMenusComponent } from '@bazis/shared/components/context-menu/context-menus.component';
import { SimpleContextMenuComponent } from '@bazis/shared/components/context-menu/simple-context-menu.component';
import { BazisDropdownActionsComponent } from '@bazis/shared/components/dropdown-actions/dropdown-actions.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';

const DECLARATIONS = [];

const STANDALONE = [
    EntityPipe,
    FileSizePipe,
    ScheduleTimePipe,
    StatusPipe,
    TransitPipe,
    EnumPipe,
    EnumsPipe,
    PluralPipe,
    OrganizationPipe,
    LocalDatePipe,
    LocalNumberPipe,
    PhonePipe,
    SafePipe,
    SnilsPipe,
    ReversePipe,

    ScrollDirective,
    ColorDirective,
    ClickOutsideDirective,
    BackgroundDirective,
    IfPropertyDirective,
    IfControlDirective,
    StickyDirective,
    InView,
    FixNumberInputDirective,
    LocalRouterLinkDirective,
    SwipeDirective,
    AnchorDirective,
    DropZoneDirective,
    HrefProcessorDirective,
    EllipsisDirective,

    BreadcrumbsComponent,
    DefaultLoaderComponent,
    GlobalLoaderComponent,
    CookieComponent,
    CoordinatesComponent,

    BazisAlertComponent,
    ToastComponent,
    EmptyComponent,
    ModalComponent,
    ModalsComponent,
    DummyModalComponent,
    SimpleModalComponent,
    GalleryModalComponent,
    GalleryComponent,
    GallerySkeletonComponent,
    GallerySliderComponent,
    GallerySingleSliderComponent,

    FileInlineComponent,
    SignedFileInlineComponent,
    DocumentAccordeonComponent,
    CardContactComponent,
    CollapsibleToMoreComponent,
    ContextMenuComponent,
    ContextMenusComponent,
    SimpleContextMenuComponent,
    DateplateComponent,
    SliderComponent,
    StatusComponent,
    TabsSliderComponent,
    TimelineComponent,
    VehicleNumberComponent,

    InfiniteListComponent,
    InfiniteListSortSelectedComponent,
    LoadMoreListComponent,
    EntityStatusListComponent,
    EntityStatusListSkeletonComponent,

    DummyMobileComponent,
    BazisDropdownActionsComponent,
];

const WEB_COMPONENT_MODULES = [
    GridModule,
    RowModule,
    ColModule,
    IconModule,
    ToggleModule,
    TabBtnModule,
    TabsModule,
    ChipModule,
    BadgeModule,
    ButtonModule,
    LogoModule,
    LinkModule,
    DatepickerModule,
    TimepickerModule,
    TooltipModule,
    ToolbarModule,
    StepModule,
    AccordionModule,
    TripLineModule,
    SkeletonModule,
    RangeModule,
    ThumbnailModule,
    CardValueModule,
    SignStatusModule,
    LayoutPressFooterModule,
    InterrogationModule,
    TotopModule,
    SegmentModule,
    StarModule,
    StarsModule,
    LabelModule,
    AvatarModule,
];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ReactiveFormsModule,
        TranslocoModule,
        NgxSliderModule,
        ChartModule,
        ...WEB_COMPONENT_MODULES,
        ...STANDALONE,
    ],
    declarations: [...DECLARATIONS],
    exports: [...DECLARATIONS, ...WEB_COMPONENT_MODULES, ...STANDALONE],
})
export class BazisSharedModule {}
